// AES-256-GCM payload encryption, matching the backend (Python `cryptography`
// AESGCM). The session's encryption key (hex, from /session/init) is imported
// once; request/response bodies are encrypted so the plate, the AI prompt
// inputs, and the results are ciphertext on the wire — not readable in the
// network tab. WebCrypto's GCM output is ciphertext||tag, exactly what the
// backend expects and produces.

let cachedHex = '';
let cachedKey: CryptoKey | null = null;

function hexToBytes(hex: string): Uint8Array<ArrayBuffer> {
  const clean = hex.trim().toLowerCase();
  const out = new Uint8Array(new ArrayBuffer(clean.length >> 1));
  for (let i = 0; i < out.length; i++) out[i] = parseInt(clean.substr(i * 2, 2), 16);
  return out;
}

function bytesToB64(bytes: Uint8Array): string {
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]!);
  return btoa(bin);
}

function b64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const bin = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(bin.length));
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function importKey(keyHex: string): Promise<CryptoKey> {
  if (keyHex === cachedHex && cachedKey) return cachedKey;
  const key = await crypto.subtle.importKey('raw', hexToBytes(keyHex), 'AES-GCM', false, [
    'encrypt',
    'decrypt',
  ]);
  cachedHex = keyHex;
  cachedKey = key;
  return key;
}

export interface Envelope {
  n: string; // base64 12-byte nonce
  d: string; // base64 ciphertext||tag
}

export async function encryptJson(keyHex: string, obj: unknown): Promise<Envelope> {
  const key = await importKey(keyHex);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const plaintext = new TextEncoder().encode(JSON.stringify(obj));
  const ct = await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, plaintext);
  return { n: bytesToB64(iv), d: bytesToB64(new Uint8Array(ct)) };
}

export async function decryptJson<T>(keyHex: string, envelope: Envelope): Promise<T> {
  const key = await importKey(keyHex);
  const iv = b64ToBytes(envelope.n);
  const ct = b64ToBytes(envelope.d);
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-GCM', iv }, key, ct);
  return JSON.parse(new TextDecoder().decode(plaintext)) as T;
}
